import React, { useEffect, useState } from "react";
import { Table, Alert } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import CollapsibleSection from "./CollapsibleSection";
import { fmtAmount } from "./format";

interface CreditNotesSectionProps {
  enrollmentId: number;
}

const CreditNotesSection: React.FC<CreditNotesSectionProps> = ({ enrollmentId }) => {
  const [creditNotes, setCreditNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    EnrollmentService.getCreditNotes(enrollmentId)
      .then((res: any) => {
        if (res.data?.success) setCreditNotes(res.data.data.creditNotes || []);
        else setError(res.data?.message || "Failed to load credit notes");
      })
      .catch((err: any) =>
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load credit notes",
        ),
      )
      .finally(() => setLoading(false));
  }, [enrollmentId]);

  // Hide the block only on a genuinely empty (successful) result — never hide a
  // load failure, or the error would be invisible to the user.
  if (!loading && !error && creditNotes.length === 0) return null;

  return (
    <CollapsibleSection title="Credit Notes" count={creditNotes.length} defaultOpen={false}>
      {loading ? (
        <div className="ev-loading"><i className="fa fa-spinner fa-spin" /> Loading...</div>
      ) : error ? (
        <Alert variant="danger" style={{ fontSize: 12, marginBottom: 0 }}>
          {error}
        </Alert>
      ) : (
        <Table bordered hover size="sm" style={{ fontSize: 12, marginBottom: 0 }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>CN Number</th>
              <th style={{ textAlign: "right" }}>Amount</th>
              <th style={{ textAlign: "right" }}>Tax Amount</th>
              <th>Status</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {creditNotes.map((cn: any) => {
              const approved = !!(cn.businessApprovedBy && cn.financeApprovedBy);
              return (
                <tr key={cn.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{cn.createdDate || "--"}</td>
                  <td>{cn.creditNoteNumber}</td>
                  <td style={{ textAlign: "right" }}>{fmtAmount(cn.amount)}</td>
                  <td style={{ textAlign: "right" }}>{fmtAmount(cn.taxAmount)}</td>
                  <td>
                    <span
                      className="ev-status-badge"
                      style={{
                        background: approved
                          ? "var(--status-success-bg)"
                          : "var(--status-warning-bg)",
                        color: approved
                          ? "var(--status-success)"
                          : "var(--status-warning)",
                      }}
                    >
                      {approved ? "Approved" : "Open"}
                    </span>
                    {(cn.businessApprovedBy || cn.financeApprovedBy) && (
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                        {cn.businessApprovedBy && (
                          <div>B: {cn.businessApprovedBy} ({cn.businessApprovalDate || "--"})</div>
                        )}
                        {cn.financeApprovedBy && (
                          <div>F: {cn.financeApprovedBy} ({cn.financeApprovalDate || "--"})</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{cn.remarks}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </CollapsibleSection>
  );
};

export default CreditNotesSection;
